<template>
  <b-overlay :show="isLoading" bg-color="transparent" blur="2px">
    <!-- LOADING OVERLAY -->
    <template #overlay>
      <img src="@/assets/lottie/plant.gif" style="width: 5em" />
    </template>
    <b-card no-body class="pt-5 rounded-xl mb-5">
      <!-- CARD HEADER -->
      <b-card-header
        class="border-2 border-bottom px-5 pt-0 pb-3 d-flex align-items-center"
        v-if="title"
      >
        <a
          v-if="!$store.state.unknownRoute && showBack"
          @click="$router.go(-1)"
          class="btn btn-transparent btn-lg btn-icon btn-back mr-1"
          v-b-tooltip.top="$t('global.go_back')"
        >
          <i class="ri-arrow-left-circle-line text-primary p-0"></i>
        </a>
        <b-card-title v-if="title" class="mb-0">
          {{ title }}
        </b-card-title>
        <b-btn
          v-if="showRefresh && api"
          class="ml-2 py-2 rounded-full"
          size="sm"
          variant="light-info"
          :disabled="is_refresh"
          @click="refreshData"
        >
          <i class="ri-refresh-line" :class="{ 'spin-animation': is_refresh }"></i>
        </b-btn>
        <div class="ml-auto"></div>
        <slot name="header-actions"></slot>
        <b-btn
          v-if="showCreate"
          class="ml-2 py-2"
          size="sm"
          variant="light-primary"
          @click="$emit('onCreate')"
        >
          <i class="ri-add-line"></i>
          Tambah
        </b-btn>
        <b-btn
          v-if="showEdit"
          class="py-2"
          size="sm"
          variant="light-warning"
          @click="$emit('onEdit')"
        >
          <i class="ri-edit-line"></i>
          Edit
        </b-btn>
        <b-btn
          v-if="showDelete"
          class="ml-2 py-2"
          size="sm"
          variant="light-danger"
          @click="$emit('onDelete')"
        >
          <i class="ri-delete-bin-line"></i>
          Hapus
        </b-btn>
      </b-card-header>
      <!-- CARD BODY -->
      <b-modal
        :id="'image-preview-modal'+previewId"
        hide-footer
        hide-header
        centered
        size="xl"
        modal-class="image-preview-modal"
        content-class="bg-transparent border-0 shadow-none"
      >
        <button class="preview-close-btn" @click="$bvModal.hide('image-preview-modal'+previewId)">X</button>
        <img v-img-fb="previewImage" alt="img-zoom" class="img-fluid rounded mx-auto d-block"/>
      </b-modal>
      <b-card-body class="pt-5 pb-0">
        <slot name="card-body">
          <!-- get from api component -->
          <div v-if="api">
            <div class="table-scroll-wrapper">
              <b-table
                :fields="tempFields"
                :items="table.data"
                :per-page="table.perPage"
                :current-page="table.currentPage"
                @filtered="onTableFiltered"
              >
                <template v-for="(field, fIndex) in tempFields" v-slot:[`cell(${field.key})`]="{ item, index }">
                  
                  <span v-if="field.key === 'index'">
                    {{ index + (table.currentPage - 1) * table.perPage + 1 }}.
                  </span>
  
                  <span v-else-if="field.type === 'image'">
                    <img                    
                      v-if="item[field.key]"
                      class="img-table"
                      :src="item[field.key].url || item[field.key]"
                      :alt="field.label"
                      @click="openImagePreview(item[field.key])"
                    />
                  </span>
                  <span v-else-if="field.type === 'map'">
                    <base-map-coordinate-loader
                      :title="field.label"
                      :coordinate_data="item[field.key]"
                    />
                  </span>
                  <span v-else-if="field.key === '__actions'">
                    <slot name="row-actions" :rowData="item">
                      <div class="d-flex">
                        <b-btn
                          v-if="rowShowDetail"
                          size="sm"
                          variant="light-info"
                          class="mr-1"
                          @click="$emit('rowDetail', item)"
                        >
                          <i class="ri-eye-line"></i>
                        </b-btn>
                        <b-btn
                          v-if="rowShowEdit"
                          size="sm"
                          variant="light-warning"
                          class="mr-1"
                          @click="$emit('rowEdit', item)"
                        >
                          <i class="ri-edit-line"></i>
                        </b-btn>
                        <b-btn
                          v-if="rowShowDelete"
                          size="sm"
                          variant="light-danger"
                          @click="$emit('rowDelete', item)"
                        >
                          <i class="ri-delete-bin-line"></i>
                        </b-btn>
                        <slot name="custom-action-slot" v-bind="item">
                        </slot>
                      </div>
                    </slot>
                  </span>
                  <span v-else>
                    <slot
                      v-if="$scopedSlots[field.key]"
                      :name="field.key"
                      :rowData="item"
                    ></slot>

                    <template v-else>
                      <template v-if="field.is_long_text">
                        {{
                          getDisplayText(
                            item[field.key],
                            field,
                            isRowExpanded(`${item.id}-${field.key}`)
                          )
                        }}

                        <a
                          v-if="
                            item[field.key] &&
                            String(item[field.key]).length > (field.max_length || 30)
                          "
                          href="#"
                          class="ml-1"
                          @click.prevent="
                            toggleRowExpanded(`${item.id}-${field.key}`)
                          "
                        >
                          {{
                            isRowExpanded(`${item.id}-${field.key}`)
                              ? 'See Less'
                              : 'See More'
                          }}
                        </a>
                      </template>
                      <template v-else>
                        {{ item[field.key] }}
                      </template>
                    </template>
                  </span>
                </template>
              </b-table>
            </div>
            <b-pagination
              v-if="!hidePagination"
              :total-rows="table.totalRows"
              :per-page="table.perPage"
              v-model="table.currentPage"
            ></b-pagination>
          </div>
          <!-- default type -->
          <div v-else>
            <table v-if="!isLoading" class="table table-detail">
              <tbody>
                <tr
                  v-for="(field, fIndex) in tempFields"
                  :key="`row-detail-${fIndex}`"
                >
                  <td class="nowrap-table">
                    {{ field.label }}
                  </td>
                  <td class="nowrap-table">&emsp;:&nbsp;</td>
                  <!-- image type -->
                  <td v-if="field.type == 'image'" class="">
                    <img
                      class="img-table"
                      v-img-fb="
                        mainData[field.key] ? mainData[field.key].url : ''
                      "
                      :alt="field.label"
                    />
                  </td>
                  <td v-else-if="field.type == 'map'" class="">
                    <base-map-coordinate-loader
                      v-if="field.coordinate && typeof field.coordinate === 'object'"
                      :title="field.label"
                      :coordinate_data="getCoordinateData(field)"
                    />
                  </td>
                  <td v-else-if="field.type == 'single-polygon'" class="">
                    <base-single-polygon-loader
                      v-if="field.coordinate && typeof field.coordinate === 'object'"
                      :title="field.label"
                      :coordinate_data="getCoordinateData(field)"
                    />
                  </td>
                  <td v-else-if="field.type == 'multi-polygon'" class="">
                    <base-multi-polygon-loader
                      v-if="field.coordinate && typeof field.coordinate === 'object'"
                      :title="field.label"
                      :coordinate_data="getCoordinateData(field)"
                      :polygons_data="getPolygonsData(field)"
                    />
                  </td>
                  <td v-else class="detail-value-cell">
                    <div class="detail-value-wrapper">
                      <slot
                        v-if="$scopedSlots[field.key]"
                        :name="field.key"
                        :rowData="mainData"
                      ></slot>

                      <template v-else>
                        <span :class="classReader(mainData[field.key], field.classes)">
                          <span v-if="field.transform">
                            {{ mainData[field.key] | parse(field.transform) }}
                          </span>
                          <span v-else>
                              <span v-if="field.prefix">
                                {{ field.prefix }}
                              </span>
                              {{ mainData[field.key] }}
                              <span v-if="field.suffix">
                                {{ field.suffix }}
                              </span>
                          </span>
                        </span>
                      </template>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </slot>
      </b-card-body>
    </b-card>
  </b-overlay>
</template>

<script>
export default {
  name: "GrowerWebCardDetail",

  props: {
    title: {
      type: String,
      default: "",
    },
    showBack: {
      type: Boolean,
      default: false,
    },
    showCreate: {
      type: Boolean,
      default: false,
    },
    showEdit: {
      type: Boolean,
      default: false,
    },
    showDelete: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: "",
    },
    coordinate: {
      type: Object,
      default: () => ({}),
    },
    data: {
      type: Object,
      default: () => ({}),
    },
    fields: {
      type: Array,
      default: () => [],
    },
    api: {
      type: String,
      default: "",
    },
    apiParams: {
      type: Object,
      default: () => ({}),
    },
    hideIndex: {
      type: Boolean,
      default: false,
    },
    hidePagination: {
      type: Boolean,
      default: false,
    },
    showRowActions: {
      type: Boolean,
      default: false
    },
    rowShowDetail: {
      type: Boolean,
      default: true
    },
    rowShowEdit: {
      type: Boolean,
      default: true
    },
    rowShowDelete: {
      type: Boolean,
      default: true
    },
    showRefresh: {
      type: Boolean,
      default: false
    },
  },

  computed: {
    tempFields() {
      if (this.fields.length) {
        const newFields = [];
        if (this.api && !this.hideIndex) {
          newFields.push({
          label: "No",
          key: "index",
          thStyle: { width: '60px', textAlign: 'center' },
          tdClass: 'text-center',
        });
          if (this.api && this.showRowActions) {
            newFields.push({
              key: "__actions",
              label: "Actions",
              thStyle: { width: "160px" }
            });
          }
        }

        newFields.push(...this.fields);

        return newFields;
      } else {
        const newFields = [];
        for (const [field, val] of Object.entries(this.data)) {
          newFields.push({
            label: field,
            key: field,
          });
        }
        return newFields;
      }
    },
  },


  watch: {
    data: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val) {
          this.mainData = {
            ...this.mainData,
            ...val,
          };
        }
      },
    },
  },

  data() {
    return {
      isLoading: false,
      is_refresh: false,
      previewImage: null,
      previewId: null,
      mainData: {},
      expandedFields: {},
      expandedRows: {},
      table: {
        data: [],
        currentPage: 1,
        perPage: 10,
        totalRows: 0,
      },
    };
  },

  mounted() {
    if (this.api) {
      this.getData();
    }
  },

  methods: {
    isRowExpanded(rowKey) {
      return this.expandedRows[rowKey] === true;
    },

    toggleRowExpanded(rowKey) {
      this.$set(
        this.expandedRows,
        rowKey,
        !this.expandedRows[rowKey]
      );
    },
    isExpanded(fieldKey) {
      return this.expandedFields[fieldKey] === true;
    },

    toggleExpanded(fieldKey) {
      this.$set(
        this.expandedFields,
        fieldKey,
        !this.expandedFields[fieldKey]
      );
    },

    getDisplayText(value, field, expanded = false) {
      if (!value) return "";

      const text = String(value);
      const limit = field.max_length || 30;

      if (
        !field.is_long_text ||
        expanded ||
        text.length <= limit
      ) {
        return text;
      }

      return text.substring(0, limit) + "...";
    },
    async refreshData() {
      try {
        this.is_refresh = true;
        await this.getData();
      } catch (err) {
        console.error(err);
      } finally {
        this.is_refresh = false;
      }
    },
    openImagePreview(data) {
      this.previewImage = data.url;
      this.previewId = data.url.at(-1);
      console.log('image id', data.url.at(-1))
      
      this.$nextTick(() => {
        this.$bvModal.show('image-preview-modal' + this.previewId);
      });
    },
    classReader(value, classes) {
      if (classes instanceof Object) {
        if (value instanceof Array) {
          value.forEach((val) => {
            return classes[val];
          });
        } else return classes[value];
      } else return classes;
    },
    getCoordinateData(field) {
      if (field.coordinate && this.mainData) {
        const latKey = field.coordinate.latitude;
        const lonKey = field.coordinate.longitude;

        return {
          latitude: this.mainData[latKey] || 0,
          longitude: this.mainData[lonKey] || 0,
        };
      }
      return { latitude: 0, longitude: 0 };
    },
    getPolygonsData(field) {
      if (field.polygon && this.mainData) {
        const polygonKey = field.polygon;
        const polygons = this.mainData[polygonKey];

        if (Array.isArray(polygons) && polygons.length > 0) {
          return polygons;
        }
      }

      return [];
    },
    getData: async function () {
      try {
        this.isLoading = true;

        const res = await this.$_api.get(this.api, this.apiParams);
        this.table.data = res.data;
        this.table.totalRows = this.table.data.length;
      } catch (err) {
        console.log("getData() error", err);
        this.$_alert.error(err);
      } finally {
        this.isLoading = false;
      }
    },
    onTableFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.table.totalRows = filteredItems.length;
      this.table.currentPage = 1;
    },
  },
};
</script>

<style scoped>
.b-card-body {
  overflow-x: visible;
}

.table-detail td:first-child {
  width: 240px;
  white-space: nowrap;
  vertical-align: top;
}

.detail-value-cell {
  max-width: 100%;
  vertical-align: top;
}

.detail-value-wrapper {
  display: block;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: normal;
}

.detail-value-wrapper > * {
  max-width: 100%;
}

.img-table {
  height: 150px;
  width: 150px;
  min-width: 150px;
  border-radius: 8px;
  object-fit: cover;
  cursor: pointer;
  margin-right: 1rem;
}
.table-scroll-wrapper {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}
.spin-animation {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>


// WEBPACK FOOTER //
// src/base/component/card-detail.vue